import React, { useEffect, useRef, useState } from 'react';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import { TestCaseModule } from '@/components/module/TestCaseModule';
import {
  ExtendedTableLowCode,
  ExtendedTableLowCodeRef,
} from '@/components/shared/table/ExtendedTableLowCode';
import { Loading } from '@/components/Loading';
import { ProjectService } from '@/api/project';
import { useParams } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { ITestRunSchema } from '@/api/testRun';
import _ from 'lodash';
import { TestCaseService } from '@/api/testCase';
import { IFilterSpec } from '@/components/shared/filter/TableFilterPanel';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';

interface BulkTestCaseFormProps {
  testCaseIds?: string[];
  moduleIds?: string[];
}

export const BulkTestCaseForm: React.FC<BulkTestCaseFormProps> = ({
  testCaseIds = [],
  moduleIds = [],
}) => {
  const { id } = useParams();
  const form = useFormContext<ITestRunSchema>();
  const ref = useRef<ExtendedTableLowCodeRef>(null);
  const spec = useCustomSpec('/api/crud/test-case', {
    columns: [
      { key: 'case_code', label: 'Case Code' },
      { key: 'title', label: 'Title' },
      { key: 'total_step', label: 'Step' },
    ],
  });
  const { data: testCaseData, isFetching: isTestCaseFetching } =
    TestCaseService.useAll({
      filter: { project_id: id, is_archive: 'false' },
    });
  const { data: projectData, isFetching: isProjectFetching } =
    ProjectService.useGet(id);

  const [selectedRow, setSelectedRow] = useState<string[]>([]);
  const [checkedModules, setCheckedModules] = useState<string[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState<string | undefined>(
    undefined,
  );

  const isFetching = isProjectFetching || isTestCaseFetching;

  const filterSpec: IFilterSpec[] = [
    { name: 'case_code', label: 'Case Code', type: 'text' },
    { name: 'title', label: 'Title', type: 'text' },
  ];

  useEffect(() => {
    setSelectedRow(testCaseIds);
    setCheckedModules(moduleIds);
  }, []);

  useEffect(
    () => form.setValue('module_ids', checkedModules),
    [checkedModules],
  );

  useEffect(() => form.setValue('test_case_ids', selectedRow), [selectedRow]);

  const handleSelectedModule = (values: string[], prevValues: string[]) => {
    const selectedTestCases = _(testCaseData)
      .filter(e => values.includes(e.module_id))
      .map(e => e.id!)
      .value();

    const removedTestCases = _(testCaseData)
      .filter(e => _.difference(prevValues, values).includes(e.module_id))
      .map(e => e.id!)
      .value();

    setSelectedRow(prevState => {
      const updatedTestCases = _.uniq([
        ...prevState,
        ...selectedTestCases,
      ]).filter(id => !removedTestCases.includes(id));

      return updatedTestCases;
    });
  };

  if (isFetching) {
    return <Loading />;
  }

  return (
    <div className="flex flex-row h-screen mb-32">
      <div className="w-[30%]">
        <TestCaseModule
          isViewer
          showCheckbox
          checkedModules={checkedModules.map(id => ({ id, module_name: '' }))}
          project={projectData!}
          onSelectedModule={module => {
            setSelectedModuleId(module.id);

            ref.current?.filter({ module_id: module.id });
            ref.current?.show();
          }}
          onCheckedModule={modules => {
            const moduleIds = _.map(modules, 'id') as string[];
            const prevModuleIds = checkedModules;

            setCheckedModules(moduleIds);

            if (modules?.[0]?.module_name.includes('All Module')) {
              // All Module dicentang, pilih semua test case
              setSelectedRow(testCaseData!.map(tc => tc.id!));
            } else {
              // Filter module berdasarkan module yang dicentang
              handleSelectedModule(moduleIds, prevModuleIds);
            }
          }}
        />
      </div>

      <div className="w-[70%]">
        {!selectedModuleId && (
          <div className="py-6 text-center">Select a module or submodule</div>
        )}

        <ExtendedTableLowCode
          ref={ref}
          hidden
          spec={spec}
          defaultFilter={{
            module_id: selectedModuleId || undefined,
            project_id: id,
            is_archive: 'false',
          }}
          defaultSort="case_code"
          defaultSortOrder="asc"
          customButtonCreate={HideCustomCreateButton}
          hideActionColumn
          showCheckbox
          selectedRow={selectedRow}
          setSelectedRow={prev => {
            // handle bulk selected
            if (Array.isArray(prev)) {
              const prevState = prev as string[];
              const newState = [...prevState, ...selectedRow];
              setSelectedRow(_.uniq(newState));
              return;
            }

            setSelectedRow(prev);
          }}
          showFilter
          filterSpec={filterSpec}
        />
      </div>
    </div>
  );
};
